import { useCallback } from "react";

import { useManualPreset } from "@vericus/cadmus-manual";

import { create, useModal } from "@ebay/nice-modal-react";

import { useAppDispatch, useAppSelector } from "@/data/hooks";
import {
  markExamAsOver,
  selectAllowGenerativeAi,
  selectAssessmentNoun,
  selectLateSubmissionTimeLimit,
} from "@/features/assignment";
import {
  acceptSubmissionDeclaration,
  closeSubmitPrompt,
  declineSubmissionDeclaration,
  selectHasAcceptedSubmissionDeclarations,
  selectHasFinal,
  submit,
} from "@/features/authority";
import { useGenaiGuidelineNotice } from "@/graphql/hooks";
import { selectCanSubmitLateExam } from "@/stores/selectors";
import { useWorkContext } from "@/stores/work-context";

import { TimedSubmitModal } from "./timed-submit-modal";

/**
 * NiceModal connected TimedSubmitModal.
 */
export const ConnectedTimedSubmitModal = create<typeof TimedSubmitModal>(() => {
  const modal = useModal();
  const dispatch = useAppDispatch();

  const hasFinal = useAppSelector(selectHasFinal);
  const canSubmitLateExam = useAppSelector(selectCanSubmitLateExam);
  const extraSubmissionTime = useAppSelector(selectLateSubmissionTimeLimit);
  const assessmentNoun = useAppSelector(selectAssessmentNoun);

  const requireGenAiAgreement =
    useAppSelector(selectAllowGenerativeAi) === true;

  const hasAcceptedSubmissionDeclarations = useAppSelector(
    selectHasAcceptedSubmissionDeclarations
  );

  const { workId } = useWorkContext();
  const genaiGuidelineNotice = useGenaiGuidelineNotice(workId);

  const openManualPreset = useManualPreset();

  return (
    <TimedSubmitModal
      workId={workId}
      open={modal.visible}
      onExitComplete={modal.remove}
      hasFinal={hasFinal}
      canSubmitLateExam={canSubmitLateExam}
      extraSubmissionTime={extraSubmissionTime}
      requireGenAiAgreement={requireGenAiAgreement}
      hasCheckedPledges={hasAcceptedSubmissionDeclarations}
      genaiGuidelineNotice={genaiGuidelineNotice}
      assessmentNoun={assessmentNoun}
      onOpenManualPreset={openManualPreset}
      onUpdateSubmissionDeclaration={useCallback(
        (submissionDeclaration, hasAgreed) => {
          if (hasAgreed) {
            dispatch(acceptSubmissionDeclaration({ submissionDeclaration }));
          } else {
            dispatch(declineSubmissionDeclaration({ submissionDeclaration }));
          }
        },
        [dispatch]
      )}
      onSubmitNowClick={() => {
        dispatch(submit({}));
        modal.hide();
      }}
      onSubmitLateClick={() => {
        dispatch(closeSubmitPrompt());
        if (!canSubmitLateExam) {
          dispatch(markExamAsOver());
        }
        modal.hide();
      }}
    />
  );
});
